/* eslint-disable import/prefer-default-export */
import axios from '../axios';

// ---------------------------------------------------------------------------
// Routing Schedule — Service Layer (v1)
//
// Camada fina sobre o axios singleton. Faz parsing do envelope JSend e renomeia
// campos em snake_case → camelCase apenas nas FRONTEIRAS (paginação, raiz do
// envelope). Campos das linhas (order_number, customer_name, route_sequence,
// active_events, etc.) são mantidos em snake_case para preservar simetria com
// o resto do módulo.
//
// Tratamento de erros NÃO acontece aqui — é responsabilidade das páginas
// (toast, fieldErrors). Helper mapFieldErrors abaixo facilita extrair o
// errors[{field,message}] do 422 padrão do backend.
//
// Endpoints v1.1 — M1+M5 (handoff frontend 2026-06-15):
//   GET    /routing/schedules/:date/:period                                   (snapshot)
//   POST   /routing/schedules/:date/:period/subclusters                       (criar rota)
//   PATCH  /routing/schedules/:date/:period/subclusters/:id                   (editar rota)
//   DELETE /routing/schedules/:date/:period/subclusters/:id                   (remover rota)
//   PATCH  /routing/schedules/:date/:period/subclusters/:id/group-items          (membership+ordem)
//   PATCH  /routing/schedules/:date/:period/subclusters/:id/designate-technician (técnico na rota — 0042 §3)
//   PATCH  /routing/schedules/:date/:period/subclusters/:id/assign-route         (propaga aos itens — 0042 §4)
//   PATCH  /routing/schedules/:date/:period/subclusters/:id/revert-assigned-route (destrava — 0042 §5)
//   POST   /routing/schedules/:date/:period/items/:itemId/events
//   PATCH  /routing/schedules/:date/:period/events/:eventId/correct
//   DELETE /routing/schedules/:date/:period/events/:eventId            (M2 — remover/destravar)
//   GET    /routing/schedules/:date/:period/items/:itemId/events
//
// O reorder antigo (PATCH .../items/reorder) foi DEPRECADO no backend (M5) —
// substituído por group-items (ordem dentro do subcluster) + assign-technician
// (técnico no subcluster). Não há mais função de reorder nesta camada.
// ---------------------------------------------------------------------------

// Base operacional default do módulo (Ponto 4). Quando o operador não escolhe
// uma base, GOIANIA é o destino da operação principal — espelha o default do
// backend. Centralizado aqui para o board, criação manual e importação
// compartilharem o mesmo valor.
export const DEFAULT_CLUSTER_ORIGIN = 'GOIANIA';

function buildSchedulePath(date, period) {
   return `/routing/schedules/${date}/${period}`;
}

// ===========================================================================
// CLUSTER ORIGINS (bases operacionais — Ponto 4)
// ===========================================================================
// GET /routing/cluster-origins (permissão routing:cluster_origin:read, já
// concedida a quem lê o board). A base é dimensão de particionamento do board
// (data/período/base) — NÃO é fronteira de segurança: quem lê o schedule vê
// TODAS as bases, o seletor é só navegação/filtro.
//
// Resposta: data.clusterOrigins = [ { id, origin_code, origin_label,
// latitude, longitude, is_active } ]. origin_code (GOIANIA/ANAPOLIS) é a
// chave estável usada como valor; origin_label é o rótulo de exibição.

export async function getClusterOrigins() {
   const response = await axios.get('/routing/cluster-origins');
   return response?.data?.data?.clusterOrigins ?? [];
}

function buildSubclusterPath(date, period, subclusterId) {
   return `${buildSchedulePath(date, period)}/subclusters/${subclusterId}`;
}

// ===========================================================================
// HELPER — mapeia errors[] em 422 para { field: message } pra inputs.
// ===========================================================================
//
// Backend devolve sempre { status: 'fail', errors: [{ field, message }] }
// em validações. Esse helper permite que a UI faça
//
//    setFieldErrors(mapFieldErrors(err));
//
// sem repetir a lógica de extração em cada chamada.

export function mapFieldErrors(error) {
   const errors = error?.response?.data?.errors;
   if (!Array.isArray(errors)) return {};
   const result = {};
   errors.forEach((entry) => {
      if (entry?.field) result[entry.field] = entry.message;
   });
   return result;
}

// ===========================================================================
// SNAPSHOT (GET — agregado dia/período)
// ===========================================================================
// Resposta crua (handoff M1+M5 §2.1):
//   data: {
//     schedule: { id, schedule_date, schedule_period, schedule_status,
//                 last_optimized_at, last_optimized_by_user_id, updated_at },
//     summary:  { total_items, total_routed, total_unrouted, total_order_value },
//     technicians: [ { id, full_name, department, is_assigned } ],  ← universo elegível
//     subclusters: [ { id, subcluster_label, subcluster_sequence,
//                      assigned_technician|null, vehicle|null,
//                      volumetry_limit_value, travel_time_limit_minutes,
//                      service_time_limit_minutes, items[], summary } ],
//     unrouted: [ <item>, ... ],
//   }
//
// Cada item (em subclusters[].items[] e unrouted[]) carrega route_sequence,
// estimated_arrival_time, customer_window_*, active_events[] e os campos
// clássicos do schedule_item (order_number, etc.) — shape inalterado.
//
// O backend cria o schedule on-demand se não existir — GET é idempotente.
//
// Ponto 4: cluster_origin (código GOIANIA/ANAPOLIS) é OBRIGATÓRIO — sem ele
// o backend devolve 422 (base inexistente → 404). O snapshot escopa subclusters/
// items/unrouted/technicians àquela base e devolve no topo do data:
//   cluster_origin: { id, origin_code, origin_label, ... }
//   schedule.cluster_origin_id

export async function getScheduleSnapshot(date, period, clusterOrigin) {
   const response = await axios.get(buildSchedulePath(date, period), {
      params: { cluster_origin: clusterOrigin },
   });
   const data = response?.data?.data ?? {};
   return {
      schedule: data.schedule ?? null,
      clusterOrigin: data.cluster_origin ?? null,
      summary: data.summary ?? {
         total_items: 0,
         total_routed: 0,
         total_unrouted: 0,
         total_cancelled: 0,
         total_order_value: 0,
      },
      technicians: data.technicians ?? [],
      subclusters: data.subclusters ?? [],
      unrouted: data.unrouted ?? [],
      // Itens CANCELADO (soft delete) daquela tripla — view-only (handoff 0048).
      // Balde separado dos vivos; alimenta o card "Itens excluídos (x)".
      cancelled: data.cancelled ?? [],
   };
}

// ===========================================================================
// LISTA DE ROTEIRIZAÇÕES (POST /routing/schedules/search)  (handoff 0036)
// ===========================================================================
// Index master-detail: uma linha por tripla (data+período+base) que JÁ tem item.
// Filtros no BODY (mesmo padrão do RoutingItemsList), NÃO query params. Permissão
// routing:schedule:read.
//
// ⚠️ "Todas/Todos" = `""` → OMITIR do body (o backend rejeita "todas"/"todos"
// como código de base — §1.4). Esta camada é a ÚNICA que traduz camel→snake e
// envia só não-vazios (regra 1.8 de navegacao.md).
//
// Resposta JSend: data.schedules[] (cada linha já traz `derived_status`
// pending|special|progress|done PRONTO — renderizar o badge com ele, NÃO derivar
// no front, §1.2) + data.summary.total_unrouted; meta { total, page, per_page,
// total_pages }. Erros: 404 not_found (base inexistente) / 422 validation.

export async function searchRoutingSchedules(args = {}) {
   const {
      clusterOrigin = '',
      schedulePeriod = '',
      dateFrom = '',
      dateTo = '',
      status = '',
      page = 1,
      perPage = 20,
      sortBy = 'schedule_date',
      sortDirection = 'desc',
   } = args;

   const body = {
      page,
      limit: perPage,
      sort_by: sortBy,
      sort_direction: String(sortDirection).toUpperCase(), // backend: ASC|DESC
   };
   // Só os não-vazios — "" (Todas/Todos) é omitido.
   if (clusterOrigin) body.cluster_origin = clusterOrigin;
   if (schedulePeriod) body.schedule_period = schedulePeriod;
   if (dateFrom) body.date_from = dateFrom;
   if (dateTo) body.date_to = dateTo;
   if (status) body.status = status;

   const response = await axios.post('/routing/schedules/search', body);
   const data = response?.data?.data ?? {};
   const meta = response?.data?.meta ?? {};
   return {
      schedules: data.schedules ?? [],
      summaryUnrouted: data.summary?.total_unrouted ?? 0,
      total: meta.total ?? 0,
      page: meta.page ?? page,
      perPage: meta.per_page ?? perPage,
      totalPages: meta.total_pages ?? 1,
   };
}

// ===========================================================================
// SUBCLUSTER CRUD (perm routing:subcluster:manage)
// ===========================================================================
// Body do create/update (handoff §3.1/§3.2) — todos os campos opcionais:
//   { vehicle_param_id, volumetry_limit_value, travel_time_limit_minutes,
//     service_time_limit_minutes, subcluster_sequence, subcluster_label,
//     route_departure_time }
//
// route_departure_time (handoff 0055 §3): T0 da rota, 'HH:MM'/'HH:MM:SS' ou
// null (limpa). A PRESENÇA do campo no body dispara recompute síncrono pago
// (reancora ETAs + regrava geometria/totais) — o chamador só envia quando o
// operador editou o horário. Aceito em qualquer route_status (muda só ETAs).
// A resposta ganha geometry_stale (mesmo contrato do group-items/move).
//
// Ponto 4: o create aceita também cluster_origin (código, opcional, default
// GOIANIA) — resolve/cria o schedule da base certa. update/delete/group-items/
// assign-technician operam por subclusterId e NÃO recebem base.
//
// create → 201 { subcluster }; update → 200 { subcluster } (não muda técnico).
// delete → 204 (sem body); 422 subcluster_not_empty se ainda tiver itens.
// create/update/delete NÃO usam ETag (só group-items usa — §4).

export async function createSubcluster(date, period, body) {
   const response = await axios.post(
      `${buildSchedulePath(date, period)}/subclusters`,
      body,
   );
   const data = response?.data?.data ?? {};
   return {
      subcluster: data.subcluster ?? null,
      geometryStale: Boolean(data.geometry_stale),
      message: response?.data?.message ?? 'Rota criada.',
   };
}

export async function updateSubcluster(date, period, subclusterId, body) {
   const response = await axios.patch(
      buildSubclusterPath(date, period, subclusterId),
      body,
   );
   const data = response?.data?.data ?? {};
   return {
      subcluster: data.subcluster ?? null,
      geometryStale: Boolean(data.geometry_stale),
      message: response?.data?.message ?? 'Rota atualizada.',
   };
}

export async function deleteSubcluster(date, period, subclusterId) {
   await axios.delete(buildSubclusterPath(date, period, subclusterId));
   // 204 sem body — nada a devolver além da confirmação.
   return { message: 'Rota removida.' };
}

// ===========================================================================
// GROUP ITEMS (PATCH — drag-and-drop persistido, substitui o reorder)
// ===========================================================================
// Payload (handoff §3.4):
//   {
//     expected_schedule_updated_at: '2026-06-11T23:32:08-03:00',  ← ETag (§4)
//     assignments: [ { item_id, sequence }, ... ],                ← membership COMPLETA
//   }
//
// assignments é a lista COMPLETA desejada do subcluster: itens hoje no subcluster
// e ausentes do payload são DESVINCULADOS (voltam pro unrouted); [] esvazia.
// sequence deve ser contígua [1..N] sem gaps nem duplicatas (senão 422).
//
// expected_schedule_updated_at é o token de concorrência otimista — vem do
// snapshot.schedule.updated_at (ou do schedule.updated_at do último group-items).
// Backend devolve 409 stale_schedule se divergente. A resposta traz o novo ETag
// em schedule.updated_at — usar como expected da próxima chamada.

export async function groupSubclusterItems(date, period, subclusterId, payload) {
   const response = await axios.patch(
      `${buildSubclusterPath(date, period, subclusterId)}/group-items`,
      payload,
   );
   const data = response?.data?.data ?? {};
   return {
      subcluster: data.subcluster ?? null,
      schedule: data.schedule ?? null,
      totalGrouped: data.totalGrouped ?? 0,
      totalUnlinked: data.totalUnlinked ?? 0,
      // geometry_stale (handoff 0034 §6): reorder DENTRO da rota agora recompõe
      // mapa/ETA server-side. true = ordem salva mas o recompute falhou (ex:
      // Google indisponível) → a polyline/ETA mostrados ficam obsoletos.
      geometryStale: Boolean(data.geometry_stale),
      message: response?.data?.message ?? 'Rota atualizada.',
   };
}

// ===========================================================================
// MOVE ITEM (PATCH — mover item entre rotas / de-para não-roteirizado)  (0034 §4)
// ===========================================================================
// Endpoint dedicado para o que o group-items NÃO cobre bem: tirar UM serviço de
// uma rota e jogá-lo em outra (ou no não-roteirizado), na posição escolhida —
// atômico, com mapa/ETA das rotas afetadas recompostos. Permissão: a mesma do
// group-items (routing:subcluster:manage).
//
// Cobre as 4 combinações: rota→rota, não-roteirizado→rota, rota→não-roteirizado
// e reposição dentro da mesma rota (mas para reorder intra-rota a UI usa
// group-items — §5). Path flat /routing/items/:itemId/move (igual cargo/car-only).
//
// Payload:
//   {
//     expected_schedule_updated_at: <ETag: schedule.updated_at do snapshot>,
//     to_subcluster_id: <id numérico da rota destino> | null  (null = dock),
//     to_sequence: <posição 1..N+1 no destino>  ← OMITIR quando to_subcluster_id=null
//   }
//
// Resposta 200 (data): { scheduleItem, source_subcluster_id, dest_subcluster_id,
// geometry_stale }. NÃO devolve o schedule novo nem o snapshot — após o move a UI
// RE-BUSCA o snapshot (traz polyline/ETAs novos e o ETag atualizado).
//
// Erros (envelope JSend errors[] com code): 409 stale_schedule · 422
// vehicle_incompatible / item_locked / item_cancelled / item_terminal /
// missing_to_sequence / move_noop / subcluster_not_in_schedule · 404
// schedule_item_not_found. As páginas tratam por errors[0].code (firstErrorCode).

export async function moveItem(itemId, payload) {
   const response = await axios.patch(`/routing/items/${itemId}/move`, payload);
   const data = response?.data?.data ?? {};
   return {
      scheduleItem: data.scheduleItem ?? null,
      sourceSubclusterId: data.source_subcluster_id ?? null,
      destSubclusterId: data.dest_subcluster_id ?? null,
      geometryStale: Boolean(data.geometry_stale),
      message: response?.data?.message ?? 'Item movido.',
   };
}

// ===========================================================================
// DESIGNAR TÉCNICO (PATCH — técnico na ROTA, planejamento)  (handoff 0042 §3)
// ===========================================================================
// ⚠️ RENOMEADO em 0042: o caminho `assign-technician` deixou de existir; usar
// `designate-technician`. Semântica: vincula o técnico à ROTA (planejamento),
// NÃO aos itens — quem propaga o técnico aos serviços é o assign-route (§4).
//
// Payload (idêntico ao antigo §3.5):
//   { assigned_technician_employee_id: 368, vehicle_param_id: 3 }  ← vehicle opcional
//   { assigned_technician_employee_id: null }                      ← desdesigna (→ RASCUNHO)
//
// 1 técnico = 1 rota por schedule (backend valida). Erros 422 (errors[].code):
//   route_locked (rota ATRIBUIDA — reverter antes) / commission_locked /
//   technician_already_in_route / technician_not_eligible / technician_not_found /
//   invalid_vehicle_param. NÃO usa ETag.
//
// Resposta 200 (data): { subcluster } com route_status = DESIGNADA (técnico !=
// null) ou RASCUNHO (null).

export async function designateSubclusterTechnician(
   date,
   period,
   subclusterId,
   payload,
) {
   const response = await axios.patch(
      `${buildSubclusterPath(date, period, subclusterId)}/designate-technician`,
      payload,
   );
   const data = response?.data?.data ?? {};
   return {
      subcluster: data.subcluster ?? null,
      message: response?.data?.message ?? 'Técnico designado.',
   };
}

// ===========================================================================
// ATRIBUIR ROTA (PATCH — "rota pronta", propaga o técnico aos itens)  (0042 §4)
// ===========================================================================
// SEM body. Propaga o técnico designado a todos os itens PENDENTE não-freelance
// da rota e marca route_status = ATRIBUIDA (trava mover/reordenar). Idempotente.
// Permissão: routing:subcluster:manage.
//
// Resposta 200 (data): { subcluster } com route_status = ATRIBUIDA. Itens
// freelance ficam intactos (concluídos manualmente — §6). RE-BUSCAR o snapshot
// depois (os itens agora têm técnico). Erros 422 (errors[].code):
//   route_not_designated (RASCUNHO) / route_already_assigned (já ATRIBUIDA).

export async function assignRoute(date, period, subclusterId) {
   const response = await axios.patch(
      `${buildSubclusterPath(date, period, subclusterId)}/assign-route`,
   );
   const data = response?.data?.data ?? {};
   return {
      subcluster: data.subcluster ?? null,
      message: response?.data?.message ?? 'Rota atribuída com sucesso.',
   };
}

// ===========================================================================
// REVERTER ATRIBUIÇÃO (PATCH — destrava p/ editar a rota)  (handoff 0042 §5)
// ===========================================================================
// SEM body. Volta ATRIBUIDA → DESIGNADA: zera o técnico dos itens PENDENTE
// (preserva os já concluídos, que congelam quem prestou o serviço) e destrava a
// membership. Permissão: routing:subcluster:manage.
//
// Pré-requisito para mover um item de uma rota já atribuída: reverter → mover →
// atribuir de novo. RE-BUSCAR o snapshot depois. Erro 422 (errors[].code):
//   route_not_assigned (rota não está ATRIBUIDA).

export async function revertAssignedRoute(date, period, subclusterId) {
   const response = await axios.patch(
      `${buildSubclusterPath(date, period, subclusterId)}/revert-assigned-route`,
   );
   const data = response?.data?.data ?? {};
   return {
      subcluster: data.subcluster ?? null,
      message: response?.data?.message ?? 'Atribuição da rota revertida com sucesso.',
   };
}

// ===========================================================================
// RECORD EVENT (POST — registrar evento de status)
// ===========================================================================
// Payload (handoff §6.3):
//   {
//     event_kind: 'OTW' | 'ARRIVED' | 'ON_SERVICE' |
//                 'EXECUTED' | 'PARTIAL' | 'CANCELLED' | 'RESCHEDULED',
//     event_timestamp: ISO-8601 com timezone (default no front: now()),
//     notes: string | null,
//     non_completion_reason_id: number | null,  // obrigatório se CANCELLED
//     freelance_confirmed: true,                 // opcional (0042 §6) — só após
//                                                // o operador confirmar no modal
//                                                // de freelance na conclusão.
//   }
//
// source / recorded_by_user_id / supersedes_event_id / gps_* são forçados pelo
// backend — front NÃO envia. freelance_confirmed é repassado como veio (a UI
// trata o 422 freelance_confirmation_required / route_not_assigned — §6).
//
// Resposta inclui { event, item: { id, item_status } } — permite update
// otimista do item sem refetch do snapshot.

export async function recordItemEvent(date, period, itemId, payload) {
   const response = await axios.post(
      `${buildSchedulePath(date, period)}/items/${itemId}/events`,
      payload,
   );
   const data = response?.data?.data ?? {};
   return {
      event: data.event ?? null,
      item: data.item ?? null,
      message: response?.data?.message ?? 'Evento registrado.',
   };
}

// ===========================================================================
// CORRECT EVENT (PATCH — supersedes)
// ===========================================================================
// Plumbed pra v1.x — UI da v1 NÃO expõe correção (decisão de produto:
// "verifique antes de confirmar", erro do operador é responsabilidade dele).
// Mantido aqui porque o endpoint existe e ligar mais tarde é trivial.

export async function correctItemEvent(date, period, eventId, payload) {
   const response = await axios.patch(
      `${buildSchedulePath(date, period)}/events/${eventId}/correct`,
      payload,
   );
   const data = response?.data?.data ?? {};
   return {
      event: data.event ?? null,
      supersededEvent: data.supersededEvent ?? null,
      item: data.item ?? null,
      message: response?.data?.message ?? 'Correção aplicada.',
   };
}

// ===========================================================================
// REMOVE EVENT (DELETE — destravamento, handoff M2 §5)
// ===========================================================================
// Permissão: routing:schedule:remove_event (gateia o botão na UI).
//
// LIFO: o backend só remove o ÚLTIMO evento ativo da timeline — a UI desabilita
// o botão nos demais (não deve nem chegar a chamar). Soft-delete: o evento sai
// da timeline ativa mas continua em auditoria.
//
// Reversão automática: remover um terminal com sintético vinculado remove o
// sintético junto (cascata) e o item reabre (volta a PENDENTE se não sobrar
// terminal ativo).
//
// Resposta (200):
//   data: {
//     removed: [ { id, event_kind, is_synthetic, ... }, ... ],  ← 1 ou 2 entradas
//     item:    { id, item_status },
//   }
// data.removed traz 2 entradas quando cascateou o sintético. A UI atualiza a
// timeline e o status do item por esse retorno.
//
// Erros: 422 event_not_last (violou LIFO) / 403 remove_event_forbidden (sem
// permissão) — tratados nas páginas via errors[].code.

export async function removeItemEvent(date, period, eventId) {
   const response = await axios.delete(
      `${buildSchedulePath(date, period)}/events/${eventId}`,
   );
   const data = response?.data?.data ?? {};
   return {
      removed: data.removed ?? [],
      item: data.item ?? null,
      message: response?.data?.message ?? 'Evento removido.',
   };
}

// ===========================================================================
// LIST ACTIVE EVENTS (GET — timeline de um item)
// ===========================================================================
// Devolve apenas eventos ATIVOS (is_superseded=0), ordenados por
// event_timestamp ASC, id ASC. Histórico completo (incluindo superseded)
// fica fora do escopo da v1.
//
// O snapshot já embute active_events[] em cada item, então este endpoint só
// é necessário para refresh manual de uma única timeline.

export async function listItemEvents(date, period, itemId) {
   const response = await axios.get(
      `${buildSchedulePath(date, period)}/items/${itemId}/events`,
   );
   return response?.data?.data?.events ?? [];
}

// ===========================================================================
// EDIÇÃO DO ITEM — cargo_class (tag) + car_only (restrição VRP)  (handoff 0032)
// ===========================================================================
// Dois eixos INDEPENDENTES, dois endpoints (não acoplar):
//   - cargo_class: rótulo informativo "A"|"B"|"C"|"D"|null. Reaproveita o
//     update geral do item (PUT). Editável exceto item CANCELADO. NÃO entra na
//     otimização do Google (ADR-003) — é só etiqueta.
//   - car_only: restrição dura "só carro" (load dimension do VRP). Endpoint
//     dedicado porque tem guarda de estado: só com item PENDENTE e a
//     roteirização ainda NÃO otimizada (one-shot). Pós-otimização, mover o
//     item para a rota de carro é ação manual no board.
// Ambos respondem 200 com data.scheduleItem = item completo serializado
// (RoutingScheduleItemSerializer.single) — use direto para update otimista.
// Permissão dos dois: routing:schedule_item:update.

// PUT /routing/items/:itemId — cargoClass ∈ "A"|"B"|"C"|"D"|null.
export async function updateItemCargoClass(itemId, cargoClass) {
   const response = await axios.put(`/routing/items/${itemId}`, {
      cargo_class: cargoClass,
   });
   return response?.data?.data?.scheduleItem ?? null;
}

// PATCH /routing/items/:itemId/car-only — body { car_only: 0|1 }. Erros 422
// com code (invalid_state_for_car_only / already_optimized) tratados na UI via
// errors[0].code.
export async function updateItemCarOnly(itemId, carOnly) {
   const response = await axios.patch(`/routing/items/${itemId}/car-only`, {
      car_only: carOnly ? 1 : 0,
   });
   return response?.data?.data?.scheduleItem ?? null;
}

// ===========================================================================
// AJUSTE MANUAL DE GEOLOCALIZAÇÃO (PATCH .../location)  (handoff 0043)
// ===========================================================================
// Itens importados sem coordenada (needs_geocoding=true) ficam barrados na
// otimização (guard geocode_required). Este endpoint grava a coordenada que o
// operador fixou no mapa — destrava o item (needs_geocoding → false) para a
// próxima otimização. Permissão: routing:schedule_item:update (a mesma do PUT
// e do car-only — sem permissão nova).
//
// Ambas as coordenadas são OBRIGATÓRIAS (meio-ponto não destrava). Faixas:
// latitude ∈ [-90, 90], longitude ∈ [-180, 180]. Precisão: o backend grava
// DECIMAL(10,7) — manda a precisão que o mapa fornecer, sem arredondar.
//
// Resposta 200: data.scheduleItem = item completo (coords preenchidas,
// needs_geocoding=false) → update otimista, igual ao cargo_class/car_only.
//
// Erros 422 (errors[0].code, tratados na UI):
//   invalid_state_for_location → item não está PENDENTE
//   already_optimized          → schedule já otimizado (one-shot não re-otimiza)
//   format                     → lat/long fora da faixa
//   required                   → faltou uma das coordenadas
// e 404 schedule_item_not_found (id inexistente).
export async function updateItemLocation(itemId, { latitude, longitude }) {
   const response = await axios.patch(`/routing/items/${itemId}/location`, {
      address_latitude: latitude,
      address_longitude: longitude,
   });
   return response?.data?.data?.scheduleItem ?? null;
}

// ===========================================================================
// SELEÇÃO DE SERVIÇOS — services[] + tempo de atendimento (handoff 0039 §3)
// ===========================================================================
// O Refiltek não traz serviço — o item nasce sem seleção. O operador associa
// ≥1 serviço do CATÁLOGO UNIFICADO (N:N, por commission_table_id + quantidade)
// e, opcionalmente, sobrescreve a duração derivada. Reaproveita o PUT geral do
// item (mesmo endpoint do cargo_class). Permissão: routing:schedule_item:update.
//
// Payload:
//   { services: [ { commission_table_id, quantity }, ... ],  ← REPLACE-SET
//     estimated_duration_minutes? }                          ← OPCIONAL (override)
//
// Regras (backend):
//   - REPLACE-SET: o array substitui a seleção inteira. Omitir `services` = não
//     mexe; enviar [] = remove tudo.
//   - commission_table_id sem repetição (422 duplicate_service).
//   - serviço inexistente/inativo → 422 invalid_service (details por id).
//   - Sem estimated_duration_minutes → backend recalcula (Σ quantity × tempo).
//   - Override de tempo EXIGE ≥1 serviço → 422 duration_without_service.
// Os 4 campos *_qty saíram do payload — não enviar mais.
// Resposta 200: data.scheduleItem = item completo serializado (update otimista).
export async function updateItemServices(itemId, { services, estimatedDurationMinutes } = {}) {
   const payload = {};
   if (Array.isArray(services)) {
      payload.services = services.map((s) => ({
         commission_table_id: s.commission_table_id,
         quantity: s.quantity,
      }));
   }
   if (estimatedDurationMinutes != null) {
      payload.estimated_duration_minutes = estimatedDurationMinutes;
   }
   const response = await axios.put(`/routing/items/${itemId}`, payload);
   return response?.data?.data?.scheduleItem ?? null;
}

// Extrai a lista de itens barrados por service_classification_required do erro
// 422 do disparo da otimização. Cada entrada de errors[] vem com field
// "item:<id>" — a UI lista os itens sem serviço para o operador classificar
// (mesmo padrão do mapGeocodeRequiredItems).
export function mapServiceClassificationItems(error) {
   const errors = error?.response?.data?.errors;
   if (!Array.isArray(errors)) return [];
   return errors
      .filter((entry) => entry?.field && String(entry.field).startsWith('item:'))
      .map((entry) => ({ field: entry.field, message: entry.message }));
}

// ===========================================================================
// OTIMIZAÇÃO — DISPARO (POST) + STATUS (GET)  (handoff 0030, v1.1)
// ===========================================================================
// Fluxo: modal de frota → optimizeSchedule (202 + job_id) → polling em
// getOptimizationStatus até completed/failed → recarregar o snapshot.
//
// Permissões: disparo = routing:schedule:manage; status = routing:schedule:read.
// cluster_origin é OBRIGATÓRIO na query (mesma base que carregou o snapshot).
//
// É ONE-SHOT por roteirização: re-disparar dá 422 already_optimized. Erros do
// disparo seguem o shape JSend fail (errors[{ field, message, code }]) — as
// páginas tratam por code (already_optimized / geocode_required / not_found /
// validation). O geocode_required NUNCA é silencioso: cada item barrado vem em
// errors[] com field "item:<id>" e a mensagem (pedido/cliente sem coordenada).

// Disparo. fleetParams = { motorcycles, max_stops_motorcycle, cars,
// max_stops_car, max_route_duration_minutes } (todos inteiros positivos;
// motos + carros ≥ 1; requested_by_user_id é derivado do token, NÃO enviar).
//
// startTime (handoff 0034 §2): "HH:MM" ou "HH:MM:SS" (hora local da base) —
// OBRIGATÓRIO. Ancora os ETAs (T0 = saída da base). Ausente/inválido → 422
// validation (campo start_time). Pré-preencher no modal com default_start_time
// da base.
//
// routeType (handoff 0057 §2): 'round_trip' (vai e volta) | 'one_way' (só vai,
// termina na última parada). OPCIONAL no contrato — omitido, o backend assume
// 'round_trip' (não-regressão). O modal sempre escolhe um valor, então enviamos
// quando presente; backend antigo (sem o campo) ignora silenciosamente.
//
// Resposta 202: data = { job_id, state: 'queued' }.
export async function optimizeSchedule(
   date,
   period,
   clusterOrigin,
   fleetParams,
   startTime,
   routeType,
) {
   const body = { fleet_params: fleetParams, start_time: startTime };
   if (routeType) body.route_type = routeType;
   const response = await axios.post(
      `${buildSchedulePath(date, period)}/optimize`,
      body,
      { params: { cluster_origin: clusterOrigin } },
   );
   const data = response?.data?.data ?? {};
   return {
      jobId: data.job_id ?? null,
      state: data.state ?? 'queued',
      message: response?.data?.message ?? 'Otimização enfileirada.',
   };
}

// Status — semáforo puro. state ∈ queued · processing · completed · failed ·
// none (none = nunca otimizado, NÃO é erro). failed traz failedReason (string)
// — exibir, nunca deixar spinner infinito. O resultado (rotas/ETAs/quem sobrou)
// NÃO vem aqui: recarregar o snapshot quando state === 'completed'.
export async function getOptimizationStatus(date, period, clusterOrigin) {
   const response = await axios.get(
      `${buildSchedulePath(date, period)}/optimize/status`,
      { params: { cluster_origin: clusterOrigin } },
   );
   const data = response?.data?.data ?? {};
   // progress: inteiro 0–100 emitido pelo worker (job.updateProgress). null
   // quando o backend ainda não expõe o campo (deploy antigo) — o overlay
   // mostra um indeterminado "Iniciando…" sem número nesse caso.
   const rawProgress = data.progress;
   const progress =
      rawProgress == null || Number.isNaN(Number(rawProgress))
         ? null
         : Math.max(0, Math.min(100, Math.round(Number(rawProgress))));
   return {
      jobId: data.job_id ?? null,
      state: data.state ?? 'none',
      progress,
      failedReason: data.failed_reason ?? null,
   };
}

// Extrai a lista de itens barrados por geocode_required do erro 422 do disparo.
// Cada entrada de errors[] com code 'geocode_required' vira { field, message }
// — a UI lista os pedidos/clientes sem coordenada para o operador geocodar.
export function mapGeocodeRequiredItems(error) {
   const errors = error?.response?.data?.errors;
   if (!Array.isArray(errors)) return [];
   return errors
      .filter((entry) => entry?.code === 'geocode_required' && entry?.field)
      .map((entry) => ({ field: entry.field, message: entry.message }));
}

// Lê o code do primeiro erro do envelope JSend fail (errors[0].code) — atalho
// genérico para os switches de UX por código (move §4, optimize §1, etc.).
export function firstErrorCode(error) {
   const errors = error?.response?.data?.errors;
   if (Array.isArray(errors) && errors[0]?.code) return errors[0].code;
   return null;
}

// Lê o code do primeiro erro do disparo (already_optimized / geocode_required /
// not_found / validation) — atalho para o switch de UX da página/modal.
export function optimizeErrorCode(error) {
   return firstErrorCode(error);
}

// Lê a mensagem do primeiro erro do envelope (errors[0].message) — usada com
// firstErrorCode para montar o toast preciso do move (§4).
export function firstErrorMessage(error) {
   const errors = error?.response?.data?.errors;
   if (Array.isArray(errors) && errors[0]?.message) return errors[0].message;
   return null;
}
